// Signal de módulo (per-tab, en memoria) para la encuesta de PERMANENCIA — la que pedimos al
// ADOPTAR la versión nueva (pasivo: menu_state='adopted'; explícito: CTA "Quedarme").
//
// POR QUÉ existe (mismo patrón que revertFeedback.ts): la encuesta debe vivir DESACOPLADA del
// ciclo de vida del chip/popover que la dispara. El CTA "Quedarme" vive en el chip de status
// (página treatment); quien RENDERIZA la encuesta es el shell del menú (MenuVersionInvitation),
// que persiste aunque la página treatment se desmonte. El chip solo "pide" mostrarla.
//
// Dos disparadores convergen en el mismo signal:
//   - PASIVO: al resolver menu_state='adopted', el hook pide la encuesta a lo sumo UNA vez de por
//     vida por (comercio, feature). El gate AUTORITATIVO es un guard DURABLE en localStorage
//     (passiveSurveyGuard, mismo patrón que analytics/surveyShownGuard) que sobrevive
//     reload/logout/cambio de sucursal; el Set de módulo `passiveShownFor` queda como dedup barato
//     dentro de la sesión. Además respeta el opt-out durable per-usuario ("No volver a preguntar").
//   - EXPLÍCITO: el CTA "Quedarme con la versión nueva" la pide tras enqueuear el switch 'kept'
//     (acción del usuario — NO pasa por el guard durable ni por el opt-out).
//
// NO se persiste el "pedido" (es un prompt de sesión: un prompt pendiente no debe sobrevivir
// logout). Lo ÚNICO durable es el hecho de que la encuesta pasiva YA se mostró (passiveSurveyGuard).

import { claimPassiveSurveyShown, __resetPassiveSurveyGuardForTests } from './passiveSurveyGuard';
import { hasOptedOutUsageFeedback } from './preview';

type Listener = () => void;

// true = hay una encuesta de permanencia pendiente de mostrar para esta feature.
const requested = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

// Dedup en-memoria del disparador PASIVO, keyed por (comercio, feature): evita tocar localStorage
// en cada resolución dentro del mismo tab. NO es el gate autoritativo (se vacía en
// reload/logout/cambio de sucursal); el once-ever de por vida lo garantiza el guard DURABLE
// (passiveSurveyGuard). Mismo patrón que invitationShownFor.
const passiveShownFor = new Set<string>();

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

export function isPermanenceSurveyRequested(featureKey: string): boolean {
  return requested.get(featureKey) === true;
}

export function subscribePermanenceSurvey(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// Pide mostrar la encuesta (omitible, fire-and-forget). Idempotente: si ya está pedida no
// re-notifica. Lo llaman tanto el CTA "Quedarme" como el disparador pasivo.
export function requestPermanenceSurvey(featureKey: string): void {
  if (requested.get(featureKey) === true) return;
  requested.set(featureKey, true);
  notify(featureKey);
}

// El shell lo llama al enviar el motivo, omitir/cerrar la encuesta, u optar por no volver.
export function clearPermanenceSurvey(featureKey: string): void {
  if (requested.get(featureKey) !== true) return;
  requested.set(featureKey, false);
  notify(featureKey);
}

// Disparador PASIVO con guard durable once-ever. Devuelve true si DISPARÓ la encuesta en esta
// llamada (primera vez de por vida para ese comercio+feature); false si ya se mostró antes o si el
// operador optó por no volver a preguntar. Sin commerce activo no dispara ni marca el guard (la
// telemetría iría sin sujeto). El caller decide cuándo invocarlo (al detectar menu_state='adopted'
// tras la hidratación) y ya excluye GA/evacuado antes de llamar.
export function maybeRequestPermanenceSurveyForAdopted(featureKey: string, commerceId: string | null): boolean {
  if (!commerceId) return false;
  // Opt-out durable per-usuario ("No volver a preguntar", compartido con la encuesta de transición):
  // hoy se ESCRIBÍA pero nunca se LEÍA en este path (era placebo). El disparador pasivo lo respeta.
  if (hasOptedOutUsageFeedback(featureKey)) return false;
  const key = `${commerceId}:${featureKey}`;
  // Dedup barato de sesión: si ya se mostró en este tab, ni siquiera tocamos localStorage.
  if (passiveShownFor.has(key)) return false;
  // Gate AUTORITATIVO durable: once-ever de por vida por (comercio, feature), sobrevive
  // reload/logout/cambio de sucursal. Si el claim falla (ya reclamado), sincronizamos el dedup de
  // sesión y NO reabrimos.
  if (!claimPassiveSurveyShown('permanence', commerceId, featureKey)) {
    passiveShownFor.add(key);
    return false;
  }
  passiveShownFor.add(key);
  requestPermanenceSurvey(featureKey);
  return true;
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal ni un prompt pendiente ni el dedup en-memoria
// del comercio anterior deben sobrevivir. NO borra el guard DURABLE (passiveSurveyGuard): ese es
// precisamente el que debe sobrevivir el cambio de sesión/sucursal para que la encuesta pasiva no
// reaparezca "a cada rato" en multisucursal. Coherente con resetRevertFeedbackState y el resto de
// resets del módulo (store/hydration/draining/preview/roleRejection/invitationShown).
export function resetPermanenceSurveyState(): void {
  const keys = Array.from(requested.keys());
  requested.clear();
  passiveShownFor.clear();
  keys.forEach((k) => notify(k));
}

// Solo para tests: limpia el estado de módulo Y el guard durable entre casos (aislamiento).
export function __resetPermanenceSurveyForTests(): void {
  requested.clear();
  listeners.clear();
  passiveShownFor.clear();
  __resetPassiveSurveyGuardForTests();
}
